import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Settings as SettingsIcon, 
  Lock, 
  ArrowLeft, 
  Eye, 
  EyeOff, 
  Flame, 
  Trophy,
  CheckCircle,
  Circle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import ReadingInsightsCard from '../components/ReadingInsightsCard';
import AvatarPicker from '../components/avatars/AvatarPicker';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const userRole = localStorage.getItem('userRole');
  const userName = localStorage.getItem('userName') || 'User';
  const userEmail = localStorage.getItem('userEmail') || '';
  const token = localStorage.getItem('token');

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Streak state
  const [streak, setStreak] = useState(null);
  const [loadingStreak, setLoadingStreak] = useState(true);

  // Avatar state — bootstrapped from localStorage, refreshed from /me on mount
  const [avatarId, setAvatarId] = useState(() => localStorage.getItem('userAvatarId') || null);
  const [avatarPhotoUrl, setAvatarPhotoUrl] = useState(() => localStorage.getItem('userAvatarPhotoUrl') || null);

  const getPasswordStrength = (pwd) => {
    if (!pwd) return { checks: [], allPassed: false };
    const checks = [
      { label: 'At least 8 characters', passed: pwd.length >= 8 },
      { label: 'Uppercase letter', passed: /[A-Z]/.test(pwd) },
      { label: 'Lowercase letter', passed: /[a-z]/.test(pwd) },
      { label: 'Number', passed: /\d/.test(pwd) },
      { label: 'Special character', passed: /[!@#$%^&*()\-_+=[\]{}|;':",./<>?`~\\]/.test(pwd) },
    ];
    return { checks, allPassed: checks.every(c => c.passed) };
  };

  useEffect(() => {
    fetchStreak();
    fetchMyAvatar();
  }, []);

  const fetchMyAvatar = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/user/me/avatar`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const id = res.data?.avatar_id || null;
      const photo = res.data?.avatar_photo_url || null;
      setAvatarId(id);
      setAvatarPhotoUrl(photo);
      if (id) localStorage.setItem('userAvatarId', id); else localStorage.removeItem('userAvatarId');
      if (photo) localStorage.setItem('userAvatarPhotoUrl', photo); else localStorage.removeItem('userAvatarPhotoUrl');
    } catch (error) {
      // Avatar restore is best-effort — still log so we can see real failures
      console.warn('[Settings] failed to restore avatar:', error?.message || error);
    }
  };

  const fetchStreak = async () => {
    try {
      const response = await axios.get(`${API}/user/streak`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStreak(response.data);
    } catch (error) {
      console.error('Error fetching streak:', error);
    } finally {
      setLoadingStreak(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({
        title: 'Missing Fields',
        description: 'Please fill in all password fields.',
        variant: 'destructive',
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: 'Passwords Do Not Match',
        description: 'New password and confirm password must match.',
        variant: 'destructive',
      });
      return;
    }

    if (newPassword.length < 8) {
      toast({
        title: 'Password Too Short',
        description: 'New password must be at least 8 characters long.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast({
        title: 'Password Changed',
        description: 'Your password has been updated successfully.',
      });

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Password change error:', error);
      toast({
        title: 'Password Change Failed',
        description: error.response?.data?.detail || 'Failed to change password. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-white hover:text-gray-300 transition-colors mb-4 inline-flex items-center"
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back
          </button>
          <div className="flex items-center space-x-3 mb-4">
            <SettingsIcon className="h-10 w-10 text-red-500" />
            <h1 className="text-4xl font-bold text-white">Account Settings</h1>
          </div>
          <p className="text-gray-400 text-lg">
            Manage your account settings, reading goals, and security.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-8">
            {/* Your Reading Insights */}
            <ReadingInsightsCard />

            {/* Reading Streak Card */}
            <div className="bg-gradient-to-br from-orange-900/30 to-red-900/30 rounded-xl p-6 border border-orange-500/30" data-testid="streak-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-white flex items-center">
                  <Flame className="h-6 w-6 text-orange-500 mr-2" />
                  Reading Streak
                </h2>
                {streak?.read_today && (
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">
                    Read Today ✓
                  </span>
                )}
              </div>

              {loadingStreak ? (
                <div className="text-gray-400 text-center py-4">Loading streak...</div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-black/30 rounded-lg p-4 text-center">
                    <Flame className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-orange-400">{streak?.current_streak || 0}</p>
                    <p className="text-sm text-gray-400">Current Streak</p>
                  </div>
                  <div className="bg-black/30 rounded-lg p-4 text-center">
                    <Trophy className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-yellow-400">{streak?.longest_streak || 0}</p>
                    <p className="text-sm text-gray-400">Longest Streak</p>
                  </div>
                </div>
              )}

              <p className="text-gray-500 text-sm mt-4 text-center">
                Read at least one book daily to keep your streak alive!
              </p>
            </div>

            {/* Account Info */}
            <div className="bg-white/5 backdrop-blur-sm rounded-lg p-6 border border-white/10">
              <h2 className="text-xl font-semibold text-white mb-4">Account Information</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Name</span>
                  <span className="text-white font-medium">{userName}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Email</span>
                  <span className="text-white font-medium">{userEmail}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Role</span>
                  <span className={`font-medium px-3 py-1 rounded-full text-sm ${
                    userRole === 'admin' 
                      ? 'bg-red-600/20 text-red-400 border border-red-600/30' 
                      : 'bg-blue-600/20 text-blue-400 border border-blue-600/30'
                  }`}>
                    {userRole === 'admin' ? 'Administrator' : 'Member'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            {/* Bookworm Avatar Picker */}
            <AvatarPicker
              token={token}
              userName={userName}
              currentAvatarId={avatarId}
              currentAvatarPhotoUrl={avatarPhotoUrl}
              onChange={({ avatarId: id, avatarPhotoUrl: photo }) => {
                setAvatarId(id);
                setAvatarPhotoUrl(photo);
              }}
            />

            {/* Change Password Form */}
            <div className="bg-white/5 backdrop-blur-sm rounded-lg p-6 border border-white/10">
              <div className="flex items-center space-x-3 mb-6">
                <Lock className="h-6 w-6 text-yellow-500" />
                <h2 className="text-xl font-semibold text-white">Change Password</h2>
              </div>

              <form onSubmit={handleChangePassword} className="space-y-4">
                {/* Current Password */}
                <div className="space-y-2">
                  <Label htmlFor="currentPassword" className="text-white">
                    Current Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? 'text' : 'password'}
                      placeholder="Enter your current password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-white">
                    New Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="Enter your new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {newPassword && (
                    <div className="mt-2 space-y-1">
                      {getPasswordStrength(newPassword).checks.map((c) => (
                        <div key={`pwd-${c.label}`} className={`flex items-center gap-1.5 text-xs ${c.passed ? 'text-green-400' : 'text-zinc-500'}`}>
                          {c.passed ? <CheckCircle className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
                          <span>{c.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {!newPassword && <p className="text-xs text-gray-500">Password must meet all strength requirements</p>}
                </div>

                {/* Confirm New Password */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-white">
                    Confirm New Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-5"
                >
                  {loading ? 'Changing Password...' : 'Change Password'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Settings;
